import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import {catchError, first, map, switchMap, tap, timeout, withLatestFrom} from 'rxjs/operators';
import { apiUrl } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import {StatoSessioneService} from './stato-sessione.service';
import {BaseFileResponse, ImageAnalysisResponse, isImageAnalysisResponse, StreamProgressEvent} from '../interfaces/fileUploadInterfaces';
import { PromptManagementService } from './prompt-management.service';

export interface NoteResponse {
  id: string;
  content: string;
  date: string;
}

export interface ValidateReusedTestsResponse {
  verdict: 'FULL_REUSE' | 'PARTIAL' | 'NO_REUSE';
  valid_test_ids: string[];
  covered_requirements: string[];
  uncovered_requirements: string[];
  test_verdicts: { test_id: string; is_valid: boolean; covers_requirements: string[]; reason: string }[];
  token_usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
}

export interface TestGenerationApiResponse {
  tests?: any[];       // The array of test objects
  columns?: string[];    // Array of column names
  token_usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number
  };
  message?: string;      // Optional message from API
}

@Injectable({
  providedIn: 'root'
})

export class ApiService {
  private apiUrl = apiUrl

  constructor(
    private http: HttpClient, private authService: AuthService,
    private statoSessioneService: StatoSessioneService,
    private promptService: PromptManagementService
  ) { }

  private getHeaders(): Observable<HttpHeaders> {
    return this.authService.token$.pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return new Observable<HttpHeaders>(observer => {
          observer.next(headers);
          observer.complete();
        });
      })
    );
  }

  analyzeCoverage(story: string, test: string, model: string): Observable<string> {
    const request = { story, test, model };
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<string>(`${this.apiUrl}/analyzecoverage/`, request, { headers })
      ),
      catchError(this.handleError)
    );
  }

  saveNotes(project: string, additional_context: string): Observable<any> {
    console.log(`notes aggiornate per il progetto ${project}`);

    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post(`${this.apiUrl}/savenotes/`, { project, additional_context }, { headers })
      ),
      catchError(this.handleError)
    );
  }

  retrieveAllNotes(project: string): Observable<NoteResponse[]> {
    console.log('progetto debug: ' + project)
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<NoteResponse[]>(`${this.apiUrl}/retrieveallnotes/`, { project }, { headers })
      ),
      catchError(this.handleError)
    );
  }

  retrieveSimilarNotes(project: string, story: string): Observable<NoteResponse[]> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<NoteResponse[]>(`${this.apiUrl}/retrieve_similar_notes/`, { project, story }, { headers })
      ),
      catchError(this.handleError)
    );
  }

  deleteNote(project: string, noteId: string): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.delete(`${this.apiUrl}/deletenote/`, {
          headers,
          body: { project, note_id: noteId }
        })
      ),
      catchError(this.handleError)
    );
  }

  updateNote(project: string, noteId: string, newContent: string): Observable<NoteResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<NoteResponse>(`${this.apiUrl}/editnote/`, { project, note_id: noteId, new_content: newContent }, { headers })
      ),
      catchError(this.handleError)
    );
  }

  uploadFile(file: File, useVision: boolean = false): Observable<BaseFileResponse | ImageAnalysisResponse> {
    const formData: FormData = new FormData();

    // 1. Append the file
    formData.append('file', file, file.name);

    // 2. Append the language (ensure it is a string and not undefined/function)
    // Note: If getSelectedLanguage is a method, add parentheses: .getSelectedLanguage()
    const language = this.statoSessioneService.getSelectedLanguage || 'en';
    formData.append('language', language.toString());

    // 3. Vision extraction flag for PDF page-by-page analysis
    if (useVision) {
      formData.append('use_vision', 'true');
    }

    console.log(`Starting file upload with language: ${language}, vision: ${useVision}`);

    return this.getHeaders().pipe(
      switchMap(headers => {
        // CRITICAL FIX FOR 422 ERROR:
        // We must remove 'Content-Type' (usually 'application/json') from the headers.
        // This lets the browser automatically set 'Content-Type: multipart/form-data'
        // AND calculate the correct 'boundary' string required by FastAPI.
        if (headers.has('Content-Type')) {
          headers = headers.delete('Content-Type');
        }

        return this.http.post<BaseFileResponse | ImageAnalysisResponse>(
          `${this.apiUrl}/uploadfile/`,
          formData,
          { headers }
        );
      }),
      map((response) => {
        // Check if token_usage exists in the response (image analysis)
        if (isImageAnalysisResponse(response)) {
          this.statoSessioneService.updateTokenUsage(response.token_usage);
          console.log('Token usage:', response.token_usage);
        }
        return response;
      }),
      tap(response => {
        console.log('Server response:', response);
        console.log('File upload completed');
      }),
      catchError(error => {
        console.error('Error during upload:', error);
        return this.handleError(error);
      })
    );
  }

  /**
   * Upload a file with NDJSON streaming progress (for vision PDFs).
   * Returns an Observable that emits StreamProgressEvent for each progress/complete/error line.
   */
  uploadFileStream(file: File, useVision: boolean = false): Observable<StreamProgressEvent> {
    return this.authService.token$.pipe(
      first(),
      switchMap(token => {
        return new Observable<StreamProgressEvent>(observer => {
          const formData = new FormData();
          formData.append('file', file, file.name);
          const language = this.statoSessioneService.getSelectedLanguage || 'en';
          formData.append('language', language.toString());
          if (useVision) {
            formData.append('use_vision', 'true');
          }

          const abortController = new AbortController();

          fetch(`${this.apiUrl}/uploadfile-stream/`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
            signal: abortController.signal,
          }).then(async response => {
            if (!response.ok) {
              observer.error(new Error(`Upload failed: ${response.status}`));
              return;
            }
            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';
              for (const line of lines) {
                if (line.trim()) {
                  try {
                    const event: StreamProgressEvent = JSON.parse(line);
                    observer.next(event);
                  } catch { /* skip malformed lines */ }
                }
              }
            }
            // Process remaining buffer
            if (buffer.trim()) {
              try {
                observer.next(JSON.parse(buffer));
              } catch { /* skip */ }
            }
            observer.complete();
          }).catch(err => {
            if (err.name !== 'AbortError') {
              observer.error(err);
            }
          });

          return () => abortController.abort();
        });
      })
    );
  }

  generateTestCases(
    project: string,
    sessione: string,
    user: string,
    language: string,
    templateDesc: string | null | undefined,
    template: any[],
    story: string,
    instructions?: string,
  ): Observable<TestGenerationApiResponse> {

    // Combina gli stream per ottenere sia i parametri che lo stato dei prompt
    return this.statoSessioneService.inferenceParams$.pipe(
      withLatestFrom(this.promptService.getPromptState('testGeneration')),
      first(), // Prendi solo i valori correnti e completa
      switchMap(([inferenceParams, promptState]) => {

        const finalTemplateDesc = (typeof templateDesc === 'string' && templateDesc.trim() !== '')
          ? templateDesc.trim()
          : 'No description provided';
        const style = 'Tags';

        const payload: any = {
          project,
          sessione,
          user,
          inference_params: inferenceParams, // Passa l'intero oggetto
          language,
          templateDesc: finalTemplateDesc,
          template,
          story,
          style,
          instructions
        };

        // La logica per i prompt personalizzati rimane identica
        if (promptState.isCustom) {
          const effectivePrompt = this.promptService.getEffectivePrompt('testGeneration');
          payload.system_prompt = effectivePrompt.system;
          payload.user_prompt = effectivePrompt.user;
          console.log('[ApiService] Using CUSTOM prompts for test generation.');
        } else {
          console.log('[ApiService] Using DEFAULT prompts for test generation.');
        }

        // La chiamata HTTP e la gestione della risposta rimangono le stesse
        return this.getHeaders().pipe(
          switchMap(headers => {
            console.log('[ApiService] Sending payload to /generatest/:', payload);

            const httpPostObservable = this.http.post<TestGenerationApiResponse>(
              `${this.apiUrl}/generatest/`,
              payload,
              { headers }
            );

            return httpPostObservable.pipe(
              timeout(300000),
              map(response => {
                if (response.token_usage) {
                  this.statoSessioneService.updateTokenUsage(response.token_usage);
                }
                response.tests = Array.isArray(response.tests) ? response.tests : [];
                response.columns = Array.isArray(response.columns) ? response.columns : [];
                return response;
              }),
              tap(processedResponse => {
                console.log('ApiService: Processed response from generateTestCases:', processedResponse);
              }),
              catchError(error => {
                if (error.name === 'TimeoutError') {
                  return throwError({ name: 'TimeoutError', message: 'Request timed out' });
                }
                return throwError(error);
              })
            );
          }),
          catchError(this.handleError)
        );
      })
    );
  }

  // ==============================================================================
  // VALIDATE REUSED TESTS — Contextual check for micro scenario reuse
  // ==============================================================================

  validateReusedTests(payload: {
    micro_scenario_string: string;
    target_macro_title: string;
    target_requirements: { id: string; description: string }[];
    existing_tests: any[];
    language: string;
  }): Observable<ValidateReusedTestsResponse> {
    return this.statoSessioneService.inferenceParams$.pipe(
      first(),
      switchMap(inferenceParams => {
        const body = {
          inference_params: inferenceParams,
          ...payload,
        };
        return this.getHeaders().pipe(
          switchMap(headers =>
            this.http.post<ValidateReusedTestsResponse>(
              `${this.apiUrl}/validate-reused-tests/`,
              body,
              { headers }
            ).pipe(
              timeout(120000),
              tap(response => {
                if (response.token_usage) {
                  this.statoSessioneService.updateTokenUsage(response.token_usage);
                }
                console.log(`[ApiService] Validate reused tests: ${response.verdict}`);
              }),
              catchError(error => {
                if (error.name === 'TimeoutError') {
                  return throwError({ name: 'TimeoutError', message: 'Validation request timed out' });
                }
                return throwError(error);
              })
            )
          ),
          catchError(this.handleError)
        );
      })
    );
  }

  saveConfiguration(projectId: string, templateName: string, templateDesc: string, columns: any): Observable<any> {
    const url = `${this.apiUrl}/saveConfiguration/`;
    console.log(`saveConfiguration called with projectId: ${projectId}, templateName: ${templateName}, templateDesc: ${templateDesc}, columns: ${JSON.stringify(columns)}`);

    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<any>(url, { projectId, templateName, templateDesc, columns }, { headers })
      ),
      catchError(this.handleError)
    );
  }

  getTemplates(projectId: string): Observable<any> {
    const url = `${this.apiUrl}/getTemplates/${projectId}`;
    console.log(`getTemplates called with projectId: ${projectId}`);

    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<any>(url, { headers })
      ),
      catchError(this.handleError)
    );
  }

  getAgentDebugDump(sessionId: string): Observable<any> {
    const url = `${this.apiUrl}/agent/debug/${encodeURIComponent(sessionId)}`;
    return this.getHeaders().pipe(
      switchMap(headers => this.http.get<any>(url, { headers })),
      catchError(this.handleError)
    );
  }

  setDefaultTemplate(projectId: string, templateName: string): Observable<any> {
    const url = `${this.apiUrl}/setDefaultTemplate/${encodeURIComponent(projectId)}/${encodeURIComponent(templateName)}`;
    console.log(`setDefaultTemplate called with projectId: ${projectId}, templateName: ${templateName}`);

    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.put<any>(url, {}, { headers })
      ),
      catchError(this.handleError)
    );
  }

  getTemplateDetails(projectId: string, templateName: string): Observable<any> {
    const url = `${this.apiUrl}/getTemplateDetails/${projectId}/${templateName}`;
    console.log(`getTemplateDetails called with projectId: ${projectId}, templateName: ${templateName}`);

    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<any>(url, { headers })
      ),
      catchError(this.handleError)
    );
  }

  deleteConfig(projectId: string, templateName: string): Observable<any> {
    const url = `${this.apiUrl}/deleteConfig/${encodeURIComponent(projectId)}/${encodeURIComponent(templateName)}`;
    console.log(`deleteConfig called with projectId: ${projectId}, templateName: ${templateName}`);

    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.delete(url, { headers })
      ),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // Network error (server is off or no network connection)
      console.error('Network error:', error.message);
    } else if (error.status >= 400 && error.status < 500) {
      // Client-side error
      console.error(`Client-side error: ${error.status}, body was: ${error.error}`);
    } else if (error.status >= 500) {
      // Server-side error
      console.error(`Server-side error: ${error.status}, body was: ${error.error}`);
    } else {
      // Other errors
      console.error(`Unexpected error: ${error.status}, body was: ${error.error}`);
    }
    return throwError('Something bad happened; please try again later.');
  }

  /** List KB files for a project. */
  listKbFiles(projectName: string): Observable<{ documents: { name: string; type: string; uploaded: string }[] }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<{ documents: { name: string; type: string; uploaded: string }[] }>(
          `${this.apiUrl}/kb/files/${encodeURIComponent(projectName)}`,
          { headers }
        )
      ),
      catchError(this.handleError)
    );
  }

  /** Search for an answer within specific KB document(s). Single agent reads all files. */
  searchInKbDocuments(projectName: string, fileNames: string[], question: string): Observable<{ found: boolean; result: any }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{ found: boolean; result: any }>(
          `${this.apiUrl}/kb/search-in-document/`,
          { project_name: projectName, file_names: fileNames, question },
          { headers }
        )
      ),
      catchError(this.handleError)
    );
  }

  /** Read full content of a KB file by project and file name. */
  getKbFileContent(projectName: string, fileName: string): Observable<{ file_name: string; content: string; content_type: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<{ file_name: string; content: string; content_type: string }>(
          `${this.apiUrl}/kb/file-content/${encodeURIComponent(projectName)}/${encodeURIComponent(fileName)}`,
          { headers }
        )
      ),
      catchError(this.handleError)
    );
  }
}
